import {
  IOverlayResponse,
  useCreateAndUploadEmeraMediaForMetaMutation,
  useCreateAndUploadOverlayFromPropertyIdForMobileMutation,
  useCreateAndUploadOverlayFromPropertyIdMutation,
  useLazyGetMediaLibraryImagesQuery,
  useUploadImageMutation,
} from '@/editor/services/http/media-library.api';
import { getValueFromLs } from '@/shared/services/localstorage';
import { EDITOR_LS_CONSTANTS } from '@/editor/shared/constants/editor-LS.constants';
import { cropVertical } from '@/editor/pages/create-ads-steps-page/components/creatives-step/services';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAppDispatch, useAppSelector } from '@/store/hooks/redux';
import {
  selectSingleCreativeMedia,
  selectOriginalOverlayImage,
  selectSelectedCreativeType,
} from '@/editor/store/selectors/creative.selectors';
import { AvailablePlatformsEnum } from '@/editor/shared/constants/available-platforms';
import CreativeTypesEnum from '@/editor/shared/constants/creatives-types.enum';
import { setOriginalOverlayImage } from '@/editor/store/reducers/creative-step.slice';
import { ExternalCompanyIdEnum } from '@/shared/constants/external-company-id.enum';

interface CropImageResult {
  success: boolean;
  data: string | null;
  error: string | null;
}

interface IMobileOverlayResponse {
  data: IOverlayResponse;
  error?: undefined;
}

export const useMobileImage = () => {
  const [isProcessing, setIsProcessing] = useState<boolean>(false);
  const [imageCreated, setImageCreated] = useState<boolean>(false);
  const [createOverlayFromPropertyId] = useCreateAndUploadOverlayFromPropertyIdMutation();
  const [createMetaMediaFromPropertyId] = useCreateAndUploadEmeraMediaForMetaMutation();
  const [createOverlayFromPropertyIdForMobile] = useCreateAndUploadOverlayFromPropertyIdForMobileMutation();

  const [uploadImage] = useUploadImageMutation();
  const creativeMedia = useAppSelector((state) => selectSingleCreativeMedia(state, AvailablePlatformsEnum.META));
  const dispatch = useAppDispatch();
  const originalOverlayImage = useAppSelector((state) =>
    selectOriginalOverlayImage(state, AvailablePlatformsEnum.META)
  );
  const companyId = useAppSelector((state) => state.propertyInfoSliceReducer.companyId);
  const [loadMediaLibrary] = useLazyGetMediaLibraryImagesQuery();
  const selectedCreativeType = useAppSelector(selectSelectedCreativeType);

  const propertyInternalId = useMemo(() => getValueFromLs(EDITOR_LS_CONSTANTS.PROPERTY_INTERNAL_ID) as string, []);
  const selectedCreativeTypeForMeta = useAppSelector((state) => state.creativeStepSliceReducer.selectedCreativeType);

  const creativeFormsState = useAppSelector((state) => state.creativeStepSliceReducer.creativeFormsState);
  const data = creativeFormsState[AvailablePlatformsEnum.META][selectedCreativeTypeForMeta];
  const imageId = getValueFromLs(EDITOR_LS_CONSTANTS.IMAGE_ID);
  const isDefaultOverlay = useAppSelector((state) => state.creativeStepSliceReducer.isOverlayDefault);

  const handleCropImage = useCallback(async (): Promise<CropImageResult> => {
    if (selectedCreativeType !== CreativeTypesEnum.SINGLE) {
      return { success: false, error: '', data: null };
    }

    if (isProcessing) {
      return { success: false, error: 'Operation in progress', data: null };
    }

    setIsProcessing(true);

    try {
      const imageToCrop = originalOverlayImage ? originalOverlayImage : creativeMedia;

      if (!imageToCrop) {
        return {
          success: false,
          error: 'No image available to crop',
          data: null,
        };
      }

      const { id, width, height } = imageToCrop;

      if (!id || !width || !height) {
        return {
          success: false,
          error: 'Invalid image properties: missing id, width, or height',
          data: null,
        };
      }

      const blob = await cropVertical(id, width, height);

      if (!blob) {
        return {
          success: false,
          error: 'Failed to crop image: blob generation failed',
          data: null,
        };
      }

      const uploadRes = await uploadImage({
        propertyId: propertyInternalId,
        extension: 'png',
        width,
        height,
        file: blob,
      });

      const res =
        !imageId && !isDefaultOverlay ? await loadMediaLibrary({ propertyId: propertyInternalId }, true) : null;

      const mobileMetaResponse =
        companyId == ExternalCompanyIdEnum.EMERA_EIE && !imageId && !isDefaultOverlay
          ? await createMetaMediaFromPropertyId({
              propertyFileId: res?.data![0]?.id as any,
              payload: {
                withSave: true,
                isMobile: true,
                localAreaName: data?.localAreaName!,
                propertyInfo: data?.propertyInfoMobile!,
                desc: data?.description!,
                brokerName: data?.employeeName!,
                brokerTitle: data?.employeeTitle!,
              },
            })
          : null;

      if (mobileMetaResponse || imageId) {
        return {
          success: true,
          data: imageId ? imageId : mobileMetaResponse!.data?.propertyFileImageId!,
          error: null,
        };
      }

      if (!uploadRes || uploadRes.data === undefined) {
        return {
          success: false,
          error: 'Failed to upload cropped image',
          data: null,
        };
      }

      // Create overlay from the uploaded image
      const mobileOverlayResponse =
        !imageId &&
        (await createOverlayFromPropertyIdForMobile({
          internalPropertyId: propertyInternalId,
          id: uploadRes.data.propertyFileId,
        }));

      if (!mobileOverlayResponse || mobileOverlayResponse.data === undefined) {
        return {
          success: false,
          error: 'Failed to create overlay from uploaded image',
          data: null,
        };
      }

      if (mobileOverlayResponse || imageId) {
        return {
          success: true,
          data: imageId ? imageId : mobileOverlayResponse!.data?.propertyFileImageId!,
          error: null,
        };
      }

      // If no overlay image, return the uploaded file ID
      if (!originalOverlayImage) {
        setIsProcessing(false);
        setImageCreated(true);
        return {
          success: true,
          data: uploadRes.data.propertyFileId,
          error: null,
        };
      }

      setIsProcessing(false);
      setImageCreated(true);

      return {
        success: true,
        data: (mobileOverlayResponse as IMobileOverlayResponse).data.propertyFileImageId,
        error: null,
      };
    } catch (error: unknown) {
      console.error('Error in handleCropImage:', error);

      const errorMessage = error instanceof Error ? error.message : 'An unexpected error occurred while cropping image';

      return {
        success: false,
        error: errorMessage,
        data: null,
      };
    }
  }, [propertyInternalId, uploadImage, createOverlayFromPropertyId, originalOverlayImage, creativeMedia]);

  useEffect(() => {
    return () => {
      setImageCreated(false);
      setIsProcessing(false);
      dispatch(setOriginalOverlayImage({ image: null }));
    };
  }, []);

  return { handleCropImage, isProcessing, imageCreated };
};
